import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Button, Logo } from '@getinapp/appetizer';
import * as S from './styles';
import { useTranslation } from 'react-i18next';
import { Hidden, Visible } from 'react-grid-system';
import { User } from '@styled-icons/boxicons-regular/User';
import { CloseOutline } from '@styled-icons/evaicons-outline/CloseOutline';
import { LogOut } from '@styled-icons/boxicons-regular/LogOut';
import { ExternalLinkOutline } from '@styled-icons/evaicons-outline/ExternalLinkOutline';
import {
  GET_IN_ADDRESS_COOKIE_KEY,
  GET_IN_AUTH_TOKEN_COOKIE_KEY,
  GET_IN_CITY_COOKIE_KEY,
  GET_IN_USER_COOKIE_KEY,
} from 'utils/constants';
import Cookies from 'js-cookie';
import { Menu } from '@styled-icons/boxicons-regular/Menu';
import { useAuth } from 'hooks/use-auth';
import useOnClickOutside from 'hooks/use-on-click-outside';
import { Event } from 'utils/helpers';
import { useSearchUnits } from 'hooks/use-search-units';

type Page = {
  isCurrentPage: boolean;
  redirect: () => void;
};

export const Header = () => {
  const router = useRouter();
  const { search, setSearch } = useSearchUnits();

  const [isOpenMobileMenu, setIsOpenMobileMenu] = useState(false);
  const [isOpenDropdownMenu, setIsOpenDropdownMenu] = useState(false);
  const ref = useRef(null);
  const { handleIsOpenLoginModal, user, setAuthUser } = useAuth();
  const { t } = useTranslation();

  const hasAddressSelected = !!Cookies.get(GET_IN_ADDRESS_COOKIE_KEY);
  const screensHideViewingIn = ['/restaurants/'];
  const showViewingIn = !screensHideViewingIn.find((screen) =>
    router.asPath.includes(screen)
  );

  const homePage: Page = {
    isCurrentPage: router.pathname === '/',
    redirect: () => {
      handleCloseMobileMenu();
      router.push('/');
    },
  };

  const locationPage: Page = {
    isCurrentPage: router.pathname === '/location',
    redirect: () => {
      handleCloseMobileMenu();
      router.push('/location');
    },
  };

  const categoriesPage: Page = {
    isCurrentPage: router.pathname === '/categories',
    redirect: () => {
      if (search) setSearch('');
      handleCloseMobileMenu();
      router.push('/categories');
    },
  };

  const myPositionPage: Page = {
    isCurrentPage: router.pathname === '/my-position',
    redirect: () => {
      handleCloseMobileMenu();
      router.push('/my-position');
    },
  };

  const myReservationsPage: Page = {
    isCurrentPage: router.pathname === '/my-reservations',
    redirect: () => {
      handleCloseMobileMenu();
      router.push('/my-reservations');
    },
  };

  const profilePage: Page = {
    isCurrentPage: router.pathname === '/profile',
    redirect: () => {
      handleCloseMobileMenu();
      router.push('/profile');
    },
  };

  const getCity = () => Cookies.get(GET_IN_CITY_COOKIE_KEY);

  const changeUserLocation = () => {
    Event.push('getin_header_change_location_click');
    router.push('/location');
  };

  const handleOpenLogin = () => {
    Event.push('getin_header_login_click');
    if (user) {
      router.push('/profile');
    } else {
      handleCloseMobileMenu();
      handleIsOpenLoginModal(true);
    }
  };

  const handleCloseMobileMenu = () => {
    if (isOpenMobileMenu) setIsOpenMobileMenu(false);
  };

  const handleLogout = () => {
    Event.push('getin_profile_logout_entered', {
      user_id: user?.id,
    });

    Cookies.remove(GET_IN_USER_COOKIE_KEY);
    Cookies.remove(GET_IN_AUTH_TOKEN_COOKIE_KEY);
    setAuthUser(null);
    handleCloseMobileMenu();
    router.reload();
  };

  useOnClickOutside(ref, handleCloseMobileMenu);

  return (
    <>
      <S.Container>
        <S.LeftContent>
          <div
            onClick={() =>
              hasAddressSelected ? homePage.redirect() : locationPage.redirect()
            }
          >
            <Logo size="small" />
          </div>
          {hasAddressSelected && showViewingIn && (
            <div>
              <span>{t('@Header.view-in')}</span>
              <b>
                {getCity()}{' '}
                <b onClick={changeUserLocation}>({t('@Header.change')})</b>
              </b>
            </div>
          )}
        </S.LeftContent>
        <Visible sm md xs>
          <div>
            {isOpenMobileMenu ? (
              <CloseOutline size={30} onClick={handleCloseMobileMenu} />
            ) : (
              <Menu size={30} onClick={() => setIsOpenMobileMenu(true)} />
            )}
          </div>
        </Visible>
        <Hidden xs sm md>
          <S.Navigation>
            <ul>
              <S.BusinessCtaButton
                href="https://restaurante.getinapp.com.br/"
                target="_blank"
              >
                Sou Restaurante
                <ExternalLinkOutline size={16} />
              </S.BusinessCtaButton>

              <S.Divider orientation="vertical" />

              {!locationPage.isCurrentPage && (
                <li
                  className={homePage.isCurrentPage ? 'active' : ''}
                  tabIndex={0}
                  onClick={homePage.redirect}
                >
                  {t('@Header.home')}
                </li>
              )}

              {hasAddressSelected && (
                <li
                  className={categoriesPage.isCurrentPage ? 'active' : ''}
                  tabIndex={0}
                  onClick={categoriesPage.redirect}
                >
                  {t('@Header.search')}
                </li>
              )}

              {user && (
                <>
                  <li
                    className={myPositionPage.isCurrentPage ? 'active' : ''}
                    tabIndex={0}
                    onClick={myPositionPage.redirect}
                  >
                    {t('@Header.my-position-in-queue')}
                  </li>
                  <li
                    className={myReservationsPage.isCurrentPage ? 'active' : ''}
                    tabIndex={0}
                    onClick={myReservationsPage.redirect}
                  >
                    {t('@Header.my-reservations')}
                  </li>
                </>
              )}
            </ul>
            {user ? (
              <div className="dropdown-wrapper">
                <div
                  className="profile-button"
                  onMouseEnter={() => setIsOpenDropdownMenu(true)}
                >
                  {user && <User size={20} color="white" />}
                </div>
                {isOpenDropdownMenu && (
                  <div
                    className="dropdown-menu"
                    onMouseLeave={() => setIsOpenDropdownMenu(false)}
                  >
                    <p
                      className={profilePage.isCurrentPage ? 'active' : ''}
                      tabIndex={0}
                      onClick={profilePage.redirect}
                    >
                      <User size={20} /> <span>{t('@Header.profile')}</span>
                    </p>
                    {user && (
                      <p tabIndex={0} onClick={handleLogout}>
                        <LogOut size={20} /> <span>{t('@Header.logout')}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Button size="small" onClick={handleOpenLogin}>
                {t('@Header.enter')}
              </Button>
            )}
          </S.Navigation>
        </Hidden>
      </S.Container>
      {isOpenMobileMenu && (
        <S.MobileMenu ref={ref}>
          <ul>
            <S.BusinessCtaButton
              isFullWidth
              href="https://restaurante.getinapp.com.br/"
              target="_blank"
            >
              Sou Restaurante
              <ExternalLinkOutline size={16} />
            </S.BusinessCtaButton>
            <S.Divider orientation="horizontal" />
            {!locationPage.isCurrentPage && (
              <li
                className={homePage.isCurrentPage ? 'active' : ''}
                tabIndex={0}
                onClick={homePage.redirect}
              >
                {t('@Header.home')}
              </li>
            )}

            {hasAddressSelected && (
              <li
                className={categoriesPage.isCurrentPage ? 'active' : ''}
                tabIndex={0}
                onClick={categoriesPage.redirect}
              >
                {t('@Header.search')}
              </li>
            )}
            {user && (
              <>
                <li
                  className={myPositionPage.isCurrentPage ? 'active' : ''}
                  tabIndex={0}
                  onClick={myPositionPage.redirect}
                >
                  {t('@Header.my-position-in-queue')}
                </li>
                <li
                  className={myReservationsPage.isCurrentPage ? 'active' : ''}
                  tabIndex={0}
                  onClick={myReservationsPage.redirect}
                >
                  {t('@Header.my-reservations')}
                </li>
              </>
            )}
            <br />
            {user && (
              <>
                <li
                  className={profilePage.isCurrentPage ? 'active' : ''}
                  tabIndex={0}
                  onClick={profilePage.redirect}
                >
                  <User size={25} /> {t('@Header.profile')}
                </li>
                <li tabIndex={0} onClick={handleLogout}>
                  <LogOut size={25} /> {t('@Header.logout')}
                </li>
              </>
            )}
          </ul>
          {!user && (
            <Button isFullWidth onClick={handleOpenLogin}>
              {t('@Header.enter')}
            </Button>
          )}
        </S.MobileMenu>
      )}
    </>
  );
};
